import React, { FC, ReactElement, ReactNode } from 'react';

import {
  BigHeader,
  MediumHeader,
  SmallHeader,
} from '../../../ui/Header/Header';
import TextLink from '../../../ui/TextLink/TextLink';
import { usePageContext } from '../../../../hooks/PageContext/usePageContext';
import { LayoutTree } from '../../DynamicLayout';
import useOrderTracking from '../../../../hooks/useOrderTracking';
import { useTheme } from '../../../../hooks/useTheme';
import { useTranslations } from '../../../../hooks/useTranslations';
import {
  NarvarClickSubTypes,
  NarvarClickTypes,
  NarvarEventComponents,
  NarvarEventTypes,
  useAnalyticsPost,
} from '../../../../hooks/Analytics';
import { useGlobalSettings } from '../../../../hooks/useGlobalSettings';
import { getClaimsPortalLink } from './utils';

export interface TrackingStatusProps {
  featureName?: string;
  layout?: LayoutTree.FeatureLayout;
}

const TrackingStatusComponent: FC<
  React.PropsWithChildren<TrackingStatusProps>
> = ({ children, featureName, layout }) => {
  const logEvent = useAnalyticsPost();
  const {
    state: {
      retailerMoniker,
      orderNumbers,
      claimPortalUrl,
      localeCountry,
      localeLanguage,
    },
  } = usePageContext();
  const {
    bodytextDefaultNone,
    linksNoneDefault,
    linksNoneHover,
    buttonsPrimaryDefault,
    headersMediumNone,
  } = useTheme();
  const { trackLinkToClaimsPortal, trackingStatusHelperText } =
    useTranslations();

  const { trackPageAssistPortalLinkEnabled } = useGlobalSettings();
  const { getCurrentShipment } = useOrderTracking();
  const currentShipment = getCurrentShipment();

  const isDelivered = currentShipment?.status === 'DELIVERED';
  // Determine the claimsPortalLink and if it should show
  const claimsPortalLink =
    trackPageAssistPortalLinkEnabled && claimPortalUrl && isDelivered
      ? getClaimsPortalLink(
          claimPortalUrl,
          retailerMoniker,
          orderNumbers?.[0],
          localeLanguage,
          localeCountry,
        )
      : '';

  const TrackingStatusWrapper = ({ children }: { children: ReactNode }) =>
    isDelivered ? (
      <BigHeader>{children}</BigHeader>
    ) : (
      <MediumHeader
        containerCustomStyle={{
          color:
            buttonsPrimaryDefault?.backgroundColor ?? headersMediumNone?.color,
        }}
      >
        {children}
      </MediumHeader>
    );

  return (
    <div>
      {children ? (
        <TrackingStatusWrapper>{children}</TrackingStatusWrapper>
      ) : null}
      {trackingStatusHelperText && (
        <p
          style={{
            marginTop: '0.5rem',
            ...bodytextDefaultNone,
          }}
        >
          {trackingStatusHelperText}
        </p>
      )}
      {claimsPortalLink && (
        <TextLink
          linkStyle={{
            sDefault: {
              ...linksNoneDefault,
              marginTop: '0.5rem',
              display: 'block',
            },
            sHover: linksNoneHover,
          }}
          href={claimsPortalLink}
          includeReferrer
          onClick={(): void =>
            logEvent({
              clickCategory: NarvarClickSubTypes.ASSIST_PORTAL_LINK,
              clickLinkDestinationUrl: claimsPortalLink,
              clickSubType: NarvarClickSubTypes.ASSIST_PORTAL_LINK,
              clickType: NarvarClickTypes.OUTGOING,
              eventComponent: NarvarEventComponents.NTH_BLOCK_TRACKING_STATUS,
              eventFeature: NarvarEventComponents.NTH_PACK_EDD_PANEL,
              eventType: NarvarEventTypes.CLICK,
              feature: featureName,
              pageComponent: NarvarEventComponents.NTH_PACK_EDD_PANEL,
              trackLayout: layout,
            })
          }
        >
          {trackLinkToClaimsPortal}
        </TextLink>
      )}
    </div>
  );
};

const TrackingStatus: FC<TrackingStatusProps> = ({
  featureName,
  layout,
}): ReactElement | null => {
  const {
    trackingStatus,
    trackingStatusNoEddTitle,
    trackingStatusScheduleDelivery,
    trackingStatusDeliveryScheduled,
  } = useTranslations();

  const { getCurrentShipment } = useOrderTracking();
  const currentShipment = getCurrentShipment();

  if (!currentShipment) return null;

  const { displayDate, scheduling } = currentShipment;

  if (scheduling?.isSchedulable && !scheduling.isScheduled) {
    return (
      <TrackingStatusComponent featureName={featureName} layout={layout}>
        {trackingStatusScheduleDelivery}
      </TrackingStatusComponent>
    );
  }

  if (scheduling?.isScheduled) {
    return (
      <TrackingStatusComponent featureName={featureName} layout={layout}>
        {trackingStatusDeliveryScheduled}
      </TrackingStatusComponent>
    );
  }

  if (!displayDate) {
    return (
      <>
        {trackingStatusNoEddTitle && (
          <SmallHeader
            as="p"
            containerCustomStyle={{ textTransform: 'uppercase' }}
          >
            {trackingStatusNoEddTitle}
          </SmallHeader>
        )}
        <TrackingStatusComponent featureName={featureName} layout={layout}>
          {trackingStatus}
        </TrackingStatusComponent>
      </>
    );
  }

  return (
    <TrackingStatusComponent featureName={featureName} layout={layout}>
      {trackingStatus}
    </TrackingStatusComponent>
  );
};

export default TrackingStatus;
